<script setup>
definePage({
  meta: {
    action: 'read',
    subject: 'subject',
  },
})

import { status } from '@/datas/subject'
import { getProjectId } from "@/utils/cdms"
import { isLockedProject } from '@/utils/project'
import { useESignatureStore } from "@/utils/stores/esignature"
import ConfirmReason from "@/views/build/common/confirmReason.vue"
import { VDataTable } from 'vuetify/labs/VDataTable'

const { t } = useI18n()

const esignatureStore = useESignatureStore()
const page = ref(1)
const itemsPerPage = ref(10)

const headers =  ref([
  // {
  //   title: 'ID',
  //   key: 'subject_id',
  // },
  {
    title: t('Site Name'),
    key: 'site_name',
  },
  {
    title: t('Subject Name'),
    key: 'name',
  },
  { title: t('Chart No'),  key: 'chart_no' },
  { title: t('Subject Number'),  key: 'subject_no' },
  { title: t('Status'),  key: 'status' },
  { title: t('Species'),  key: 'species' },
  { title: t('Breed'),  key: 'breed' },
  { title: t('Sex'),  key: 'sex' },
  { title: t('Birthday'),  key: 'birthday' },
  { title: t('Edit'),  key: 'actions', sortable: false, width: "120px" },
])

const statusText = val => {
  return status.find(({ value }) => value == val)?.text
}

const projectLocked = ref(await isLockedProject())

const {
  data: subjectsData,
  execute: fetchSubjects,
} = await useApi(createUrl('/subjects', {
  query: {
    projectId: getProjectId(),
  },
}))

const subjects = computed(() => subjectsData.value)

const updateOptions = options => {
  page.value = options.page
}

const deleteItem = (item, type="Delete") => {
  const work = type == "Delete" ? "삭제" : "복원"

  esignatureStore.$reset()
  esignatureStore.options ={
    title: work + "하는 사유를 입력해 주세요.",
    subTitle: work + " 사유",
    item,
    type,
  }
  esignatureStore.isDialogVisible = true
}

const setConfirm = async ({ result, options  }) => {

  if(result) {
    if(options.value.type == "Restore") {
      const deleteResult = await $api(`/subjects/restore/${options.value.item.id}`, { method: 'DELETE',
        body: { reason: options.value.reason } })
    } else {
      const deleteResult = await $api(`/subjects/${options.value.item.id}`, { method: 'DELETE',
        body: { reason: options.value.reason } })
    }
    fetchSubjects()
  }
}

const subjectHistory = ref([])
const isListSubjectHistoryVisible = ref(false)

const showHistory = async item => {
  const {
    data,
  } = await useApi(createUrl(`/subjects/${item.id}/history`, {
    query: {
    },
  }))

  subjectHistory.value = data.value
  isListSubjectHistoryVisible.value = true
}

const isSnackbarVisible = ref(false)

onMounted(() => {
  isSnackbarVisible.value = true
})

const search = ref('')

const filterItems = [
  { value: "site_name", title: "기관명" },
  { value: "name", title: "동물(검체)이름" },
  { value: "subject_no", title: "식별번호" },
]

const filterItem = ref('name')

const filterText = (value, query, item) => {
  return value != null &&  query != null &&
        typeof value === 'string' &&
        item.columns[filterItem.value].toLowerCase().indexOf(query.toLowerCase()) !== -1
}

watch(() => [search.value, filterItem.value], () => {
  page.value = 1
})
</script>

<template>
  <VSnackbar
    v-model="isSnackbarVisible"
    :timeout="2000"
    location="top"
  >
    등록된 동물(검체)이름 또는 식별번호를 클릭하여 데이터를 입력할 수 있습니다.
  </VSnackbar>
  <ConfirmReason @confirm="setConfirm" />
  <ListSubjectHistoryDialog
    v-model:is-dialog-visible="isListSubjectHistoryVisible"
    :history-data="subjectHistory"
  />
  <VCard>
    <VCardText>
      <div class="d-flex justify-space-between flex-wrap gap-y-4">
        <div />
        <div class="d-flex flex-row align-center flex-wrap">
          <AppSelect
            v-model="filterItem"
            :items="filterItems"
            style="width:150px;"
          />
          <VTextField
            v-model="search"
            style="width:200px;"
            class="pe-4"
            density="compact"
            label="Search"
            flat
            hide-details
            single-line
          />
          <VBtn
            v-if="$can('read', 'subject')"
            prepend-icon="tabler-plus"
            color="success"
            :disabled="projectLocked"
            @click="$router.push('/subjects/create')"
          >
            {{ $t('Add Subject') }}
          </VBtn>
        </div>
      </div>
    </VCardText>
    <VCardText>
      <VDataTable
        v-model:search="search"
        :custom-filter="filterText"
        :headers="headers"
        :items="subjects"
        density="compact"
        :items-per-page="itemsPerPage"
        :page="page"
        @update:options="updateOptions"
      >
        <template #item.id="{ item }">
          <div :class="{'deleted' : item.deleted !=0}">
            {{ item.id }}
          </div>
        </template>

        <template #item.name="{ item }">
          <div
            v-if="item.deleted"
            :class="{'deleted' : item.deleted !=0}"
          >
            {{ item.name }}
          </div>
          <RouterLink
            v-if="!item.deleted"
            :to="{ name: 'subjects-view-id', params: { id: item.id } }"
          >
            {{ item.name }}
          </RouterLink>
        </template>

        <template #item.chart_no="{ item }">
          <div :class="{'deleted' : item.deleted !=0}">
            {{ item.chart_no }}
          </div>
        </template>

        <template #item.site_name="{ item }">
          <div :class="{'deleted' : item.deleted !=0}">
            {{ item.site_name }}
          </div>
        </template>

        <template #item.subject_no="{ item }">
          <div
            v-if="item.deleted"
            :class="{'deleted' : item.deleted !=0}"
          >
            {{ item.subject_no }}
          </div>
          <RouterLink
            v-if="!item.deleted"
            :to="{ name: 'subjects-view-id', params: { id: item.id } }"
          >
            {{ item.subject_no }}
          </RouterLink>
        </template>

        <template #item.status="{ item }">
          <div :class="{'deleted' : item.deleted !=0}">
            {{ statusText(item.status) }}
          </div>
        </template>

        <template #item.species="{ item }">
          <div :class="{'deleted' : item.deleted !=0}">
            {{ item.species }}
          </div>
        </template>

        <template #item.breed="{ item }">
          <div :class="{'deleted' : item.deleted !=0}">
            {{ item.breed }}
          </div>
        </template>

        <template #item.sex="{ item }">
          <div :class="{'deleted' : item.deleted !=0}">
            <!-- {{ sex.filter(s => s.value == item.sex)[0]?.text }} -->
            {{ item.sex }}
          </div>
        </template>

        <template #item.birthday="{ item }">
          <div :class="{'deleted' : item.deleted !=0}">
            {{ item.birthday }}
          </div>
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex gap-1">
            <IconBtn @click="showHistory(item)">
              <VIcon icon="tabler-file-spreadsheet" />
              <VTooltip
                activator="parent"
                location="top"
              >
                {{ $t('History') }}
              </VTooltip>
            </IconBtn>

            <IconBtn
              v-if="!item.deleted && $can('write', 'subject')"
              :disabled="projectLocked"
              @click="$router.push({ name: 'subjects-edit-id', params: {id:item.id} })"
            >
              <VIcon icon="tabler-edit" />
              <VTooltip
                activator="parent"
                location="top"
              >
                수정
              </VTooltip>
            </IconBtn>

            <IconBtn
              v-if="item.deleted && $can('write', 'subject')"
              :disabled="projectLocked"
              @click="deleteItem(item, 'Restore')"
            >
              <VIcon
                icon="tabler-restore"
                color="error"
              />
              <VTooltip
                activator="parent"
                location="top"
              >
                {{ $t('Restore') }}
              </VTooltip>
            </IconBtn>
            <IconBtn
              v-if="!item.deleted && $can('write', 'subject')"
              :disabled="projectLocked"
              @click="deleteItem(item)"
            >
              <VIcon
                icon="tabler-trash"
                color="error"
              />
              <VTooltip
                activator="parent"
                location="top"
              >
                삭제
              </VTooltip>
            </IconBtn>
          </div>
        </template>
      </VDataTable>
    </VCardText>
  </VCard>
</template>

<style lang="scss">
.deleted {
  text-decoration: line-through;
}
</style>
